// The payment attempts of one booking user (kind, status, amount, card, failure reason, date).
import { Table, Tag, Typography } from 'antd';

import type { AdminBookingPayment } from '../../api/types';
import { PAYMENT_STATUS_COLORS, PAYMENT_STATUS_LABELS, statusLabel } from '../../data/statuses';
import { PaymentTagRow, RefundOutsideAppTag, ReleaseFailedTag } from '../../components/CancellationMoneyTags';
import { gbp, dateTime } from '../../data/format';

// SHAR-495 — the payment-attempt table that replaces the removed standalone
// "Payments" block. Used both as the expanded row of a per-user payment and for
// payments whose payer is no longer a seat-holder.
export const PaymentAttemptsTable: React.FC<{ rows: AdminBookingPayment[] }> = ({ rows }) => (
  <Table<AdminBookingPayment>
    rowKey="id"
    dataSource={rows}
    pagination={false}
    size="small"
    locale={{ emptyText: 'No payments' }}
    columns={[
      {
        title: 'Kind',
        dataIndex: 'kind',
        key: 'kind',
        render: (k: AdminBookingPayment['kind']) => (k === 'preauth' ? 'Pre-auth' : 'Charge'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (st: AdminBookingPayment['status'], p) => (
          <PaymentTagRow>
            <Tag color={PAYMENT_STATUS_COLORS[st]}>{statusLabel(PAYMENT_STATUS_LABELS, st, p.statusLabel, st)}</Tag>
            <RefundOutsideAppTag at={p.refundOutsideAppAt} />
            {/* only while the hold still stands: one that expired since is gone */}
            {st === 'authorized' && <ReleaseFailedTag at={p.releaseFailedAt} reason={p.releaseFailure} />}
          </PaymentTagRow>
        ),
      },
      {
        title: 'Amount',
        dataIndex: 'amountGbp',
        key: 'amountGbp',
        render: (v: number) => `${gbp(v)}`,
      },
      {
        title: 'Card',
        dataIndex: 'cardLast4',
        key: 'cardLast4',
        render: (v: string | null) => (v ? `•••• ${v}` : '—'),
      },
      {
        // SHAR-1289 — the WorldPay payment id, to find the payment in WorldPay's hub
        title: 'Reference',
        dataIndex: 'providerRef',
        key: 'providerRef',
        render: (v?: string | null) => (v ? <Typography.Text copyable={{ text: v }} style={{ fontSize: 12 }}>{v.slice(0, 10)}…</Typography.Text> : '—'),
      },
      {
        title: 'Failure reason',
        dataIndex: 'failureReason',
        key: 'failureReason',
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Date',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (v: string) => dateTime(v),
      },
    ]}
  />
);
